/**
 * Dev-only API timing: enable with localStorage DEBUG_API_TIMING=1 (reload).
 * In the browser console: __TF_API_TIMING__.report()
 */

import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

type ConfigWithStart = InternalAxiosRequestConfig & { __tfStart?: number };

const MAX_ENTRIES = 400;

export interface ApiTimingEntry {
  at: number;
  client: string;
  method: string;
  route: string;
  status?: number;
  ms: number;
  error?: string;
}

const entries: ApiTimingEntry[] = [];

function timingEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem('DEBUG_API_TIMING') === '1';
  } catch {
    return false;
  }
}

/** Normalize paths like /people/42/skills → /people/:id/skills */
export function normalizeClientRoute(pathname: string): string {
  return pathname.replace(/\/\d+(?=\/|$)/g, '/:id');
}

function pushEntry(e: ApiTimingEntry): void {
  entries.push(e);
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
}

export function maybeLogFetchTiming(
  client: string,
  method: string,
  url: string,
  startedPerf: number,
  status: number,
  errorLabel?: string
): void {
  if (!timingEnabled()) return;
  let route = url;
  try {
    const u = url.startsWith('http') ? new URL(url) : new URL(url, window.location.origin);
    route = normalizeClientRoute(u.pathname);
  } catch {
    route = normalizeClientRoute(url.split('?')[0]);
  }
  pushEntry({
    at: Date.now(),
    client,
    method,
    route,
    status,
    ms: Math.round(performance.now() - startedPerf),
    error: errorLabel,
  });
}

function recordAxiosTiming(
  client: string,
  config: ConfigWithStart,
  status: number | undefined,
  error?: string
): void {
  if (!timingEnabled()) return;
  const start = config.__tfStart;
  if (start === undefined) return;
  const ms = Math.round(performance.now() - start);
  const rawUrl = `${config.baseURL ?? ''}${config.url ?? ''}`;
  let route = rawUrl;
  try {
    const u = new URL(rawUrl, window.location.origin);
    route = normalizeClientRoute(u.pathname);
  } catch {
    route = normalizeClientRoute(rawUrl.split('?')[0]);
  }
  pushEntry({
    at: Date.now(),
    client,
    method: (config.method || 'get').toUpperCase(),
    route,
    status,
    ms,
    error,
  });
}

export function installAxiosTiming(instance: AxiosInstance, clientLabel: string): void {
  if (!import.meta.env.DEV) return;

  instance.interceptors.request.use((config: ConfigWithStart) => {
    if (timingEnabled()) {
      config.__tfStart = performance.now();
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      recordAxiosTiming(clientLabel, response.config as ConfigWithStart, response.status);
      return response;
    },
    (error) => {
      const cfg = error.config as ConfigWithStart | undefined;
      if (cfg) {
        const status = error.response?.status as number | undefined;
        const msg = error.message ? String(error.message) : 'request_failed';
        recordAxiosTiming(clientLabel, cfg, status, msg);
      }
      return Promise.reject(error);
    }
  );
}

function aggregateByRoute(): Map<string, { count: number; totalMs: number; maxMs: number }> {
  const m = new Map<string, { count: number; totalMs: number; maxMs: number }>();
  for (const e of entries) {
    const key = `${e.method} ${e.route}`;
    const cur = m.get(key) ?? { count: 0, totalMs: 0, maxMs: 0 };
    cur.count += 1;
    cur.totalMs += e.ms;
    cur.maxMs = Math.max(cur.maxMs, e.ms);
    m.set(key, cur);
  }
  return m;
}

function exposeGlobal(): void {
  if (!import.meta.env.DEV || typeof window === 'undefined') return;
  (window as unknown as { __TF_API_TIMING__?: typeof apiTimingGlobal }).__TF_API_TIMING__ =
    apiTimingGlobal;
}

const apiTimingGlobal = {
  entries: () => [...entries],
  clear: () => {
    entries.length = 0;
  },
  /** Print slowest endpoints by total time spent (count × avg). */
  report: () => {
    const agg = aggregateByRoute();
    const rows = [...agg.entries()].map(([route, v]) => ({
      route,
      count: v.count,
      totalMs: v.totalMs,
      avgMs: Math.round(v.totalMs / v.count),
      maxMs: v.maxMs,
    }));
    rows.sort((a, b) => b.totalMs - a.totalMs);
    console.table(rows.slice(0, 40));
    console.info(
      '[apiTiming] Tip: set localStorage.DEBUG_API_TIMING=1 and reload. Backend: API_SLOW_REQUEST_MS=2000 API_PIPELINE_TIMING=1'
    );
  },
};

exposeGlobal();
